/**
 * FilterSubSection — one searchable checkbox list inside a report filter drawer
 * (header with Select All / Clear / Show Selected, a search box, and the list).
 *
 * subDef shape:
 *   key          — working-state key passed back to onToggle/onSelectAll/onClear
 *   label        — header text; also drives the search placeholder
 *   idKey        — option field used as the (stringified) checkbox id
 *   nameKey      — option field shown as the item name
 *   subtextKey   — optional; option field shown as a second line and included in search
 *   subtextLabel — optional; prefix for the second line (e.g. 'Serial')
 *
 * Styling comes from the ar-filter-* rules in
 * pages/reports/alert-details/alert-details-page.css, which the host page must load.
 */

import { useState, useMemo } from 'react';

const FilterSubSection = ({ subDef, options, selected, onToggle, onSelectAll, onClear }) => {
    const [search, setSearch] = useState('');
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    const hasSubtext = Boolean(subDef.subtextKey);

    const filteredOptions = useMemo(() => {
        let list = options || [];
        if (showSelectedOnly) {
            list = list.filter((opt) => selected.has(String(opt[subDef.idKey])));
        }
        if (search) {
            const term = search.toLowerCase();
            list = list.filter((opt) =>
                (opt[subDef.nameKey] || '').toLowerCase().includes(term) ||
                (hasSubtext && String(opt[subDef.subtextKey] || '').toLowerCase().includes(term))
            );
        }
        return list;
    }, [options, search, subDef.idKey, subDef.nameKey, subDef.subtextKey, hasSubtext, selected, showSelectedOnly]);

    return (
        <div className="ar-filter-section">
            <div className="ar-filter-section__header">
                <span className="ar-filter-section__label">{subDef.label}</span>
                <div className="ar-filter-section__actions">
                    <button
                        className="ar-filter-section__btn"
                        onClick={() => onSelectAll(subDef.key, (options || []).map((o) => String(o[subDef.idKey])))}
                        type="button"
                    >
                        Select All
                    </button>
                    <button
                        className="ar-filter-section__btn"
                        onClick={() => onClear(subDef.key)}
                        type="button"
                    >
                        Clear
                    </button>
                    <button
                        className={`ar-filter-section__btn${showSelectedOnly ? ' ar-filter-section__btn--active' : ''}`}
                        onClick={() => setShowSelectedOnly((prev) => !prev)}
                        type="button"
                    >
                        Show Selected
                    </button>
                </div>
            </div>
            <input
                className="ar-filter-section__search"
                type="text"
                placeholder={`Search ${subDef.label.toLowerCase()}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
            <div className="ar-filter-list">
                {filteredOptions.length === 0 ? (
                    <p className="ar-filter-list__empty">
                        {showSelectedOnly ? 'No selections.' : 'No options found.'}
                    </p>
                ) : null}
                {filteredOptions.map((opt) => {
                    const id = String(opt[subDef.idKey]);
                    const checked = selected.has(id);
                    const subtextVal = hasSubtext ? opt[subDef.subtextKey] : null;
                    return (
                        <label key={id} className={`ar-filter-list__item${hasSubtext ? ' ar-filter-list__item--has-subtext' : ''}`}>
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => onToggle(subDef.key, id)}
                            />
                            {hasSubtext ? (
                                <span className="ar-filter-list__item-content">
                                    <span className="ar-filter-list__item-name">{opt[subDef.nameKey] || '(empty)'}</span>
                                    {subtextVal ? (
                                        <span className="ar-filter-list__item-subtext">
                                            {subDef.subtextLabel ? `${subDef.subtextLabel}: ` : ''}{subtextVal}
                                        </span>
                                    ) : null}
                                </span>
                            ) : (
                                <span>{opt[subDef.nameKey] || '(empty)'}</span>
                            )}
                        </label>
                    );
                })}
            </div>
        </div>
    );
};

export default FilterSubSection;
